import { Autocomplete, Box, Checkbox, TextField, Typography, useTheme } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import CustomTable from './CustomTable';
import { getAllStudents, getApprovedTeachers, getTeacherData, sendStudentUpdate } from '../../api/content-api';
import SnackbarContext from '../../store/snackbar-context';
import Loader from '../../ui/Loader';
import Teacher from '../../model/Teacher';
import AuthContext from '../../store/auth-context';
import { useNavigate } from 'react-router-dom';
import { loginPage } from '../../store/fixed-routing';
import Student from '../../model/Student';
import dayjs from 'dayjs';
import OlympicsContext from '../../store/olympics-context';
import { filter } from './StagesTable';
import EditStudentDialog from './EditStudentDialog';
import { MangedForm } from '../../ui/Form/MangedForm';
import { useCustomTheme } from '../../store/theme-context';

const StudentsStagesTable = () => {
	const [isLoading, setIsLoading] = useState(true);
	const { palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const navigate = useNavigate();
	const { setMsg } = useContext(SnackbarContext);
	const { logout, token, isTeacher, isSecretary, isAdmin, id } = useContext(AuthContext);
	const { olympics, districtFilterId } = useContext(OlympicsContext);
	const [students, setStudents] = useState<Student[]>([]);
	const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
	const [teachersChoice, setTeachersChoice] = useState<Teacher[]>([]);
	const [editedStudent, setEditedStudent] = useState<Student | null>();
	const [editedStudentAll, setEditedStudentAll] = useState<Student | null>();
	const [inProgress, setInProgress] = useState(false);
	const editable = isAdmin || olympics?.registrationConfirmationFinishDate?.isAfter(dayjs());

	const handleCloseEditStudent = () => {
		setEditedStudentAll(null);
	};

	const downloadData = () => {
		getAllStudents(token)
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setStudents(students);
					setDisplayedStudents(filter(students, districtFilterId, id));
				} else {
					console.log(response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
				setIsLoading(false);
			});
	};

	const onSuccess = () => {
		downloadData();
		setEditedStudent(null);
	};

	useEffect(() => {
		downloadData();
		if (isTeacher) {
			getTeacherData(token, id)
				.then((response) => {
					if (response.status === 200) {
						const teacher = Teacher.fromApiResponse(response.data);
						setTeachersChoice([teacher]);
					}
				})
				.catch((exception) => {
					console.error(exception);
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				});
		} else if (isSecretary) {
			getApprovedTeachers(token)
				.then((response) => {
					if (response.status === 200) {
						const teachers = response.data.map((d: any) => Teacher.fromApiResponse(d));
						setTeachersChoice(teachers);
					}
				})
				.catch((exception) => {
					console.error(exception);
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				});
		}
	}, []);

	useEffect(() => {
		setDisplayedStudents(filter(students, districtFilterId, id));
	}, [districtFilterId]);

	const handleApproveChange = () => {
		if (editedStudent) {
			setEditedStudent({
				...editedStudent,
				approved: !editedStudent.approved,
			});
		}
	};

	const handleSave = () => {
		setInProgress(true);
		if (inProgress) {
			return;
		}
		sendStudentUpdate(token, editedStudent)
			.then((response) => {
				if (response.status === 200) {
					onSuccess();
					setMsg({ msg: 'Dane zostały zaktualizowane', severity: 'success' });
				} else {
					setMsg({ msg: response.data });
				}
				setInProgress(false);
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
				setInProgress(false);
			});
	};

	const renderApproveField = (s: Student) => {
		const value = editedStudent?.id === s.id ? editedStudent.approved : s.approved;
		return (
			<Checkbox
				checked={value}
				disabled={editedStudent?.id !== s.id || s.approved}
				onChange={handleApproveChange}
			/>
		);
	};

	const handleTeachersUpdate = (teachers: Teacher[]) => {
		if (editedStudent) {
			if (teachers.length > 3) {
				setMsg({ msg: 'Uczeń może być przypisany do maksymalnie 3 nauczycieli' });
				return;
			}
			if (isSecretary) {
				setEditedStudent({ ...editedStudent, teachers: teachers });
			} else if (isTeacher) {
				let dif1 = editedStudent?.teachers.filter((item1) => !teachers.some((item2) => item2.id === item1.id));
				dif1 = dif1 ? dif1 : [];
				const dif2 = teachers.filter((item1) => !editedStudent?.teachers.some((item2) => item2.id === item1.id));
				const dif = [...dif1, ...dif2];
				if (dif.every((item) => item.wpUserId === id)) {
					setEditedStudent({ ...editedStudent, teachers: teachers });
				} else {
					setMsg({ msg: 'Brak uprawnień by zmieniać przypisanie innych nauczycieli' });
				}
			}
		}
	};

	const renderTeachersField = (s: Student) => {
		return s.id === editedStudent?.id ? (
			<Autocomplete
				multiple
				value={editedStudent?.teachers ? editedStudent?.teachers : []}
				onChange={(event: any, newValue: Teacher[]) => handleTeachersUpdate(newValue)}
				options={teachersChoice}
				disableClearable
				forcePopupIcon={false}
				getOptionLabel={(t: Teacher) => `${t.firstName} ${t.lastName}`}
				isOptionEqualToValue={(option: Teacher, value: Teacher) => {
					return option.id === value.id;
				}}
				sx={{
					width: '100%',
					fontSize: '14px',
					'& input': {
						width: '100%',
						fontSize: '14px',
						color: palette.primary.main,
					},
					'& .MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon': {
						padding: '9px !important',
						backgroundColor: 'red',
					},
					'& .MuiOutlinedInput-root': {
						width: '100%',
						'& fieldset': {
							border: 'none',
						},
					},
					'& svg': {
						color: isHighContrast ? palette.primary.light + '!important' : 'inherit',
					},
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder='Wybierz nauczyciela'
						sx={{
							width: '100%',
							'& .MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon': {
								padding: '9px !important',
								backgroundColor: 'red',
							},
						}}
					/>
				)}
				renderOption={(props, option, { inputValue }) => {
					return (
						<Box
							component={'li'}
							sx={{
								fontSize: '16px',
								p: '10px',
							}}
							{...props}
						>
							{`${option.firstName} ${option.lastName}`}
						</Box>
					);
				}}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{s.teachers.map((t: Teacher) => `${t.firstName} ${t.lastName}, `)}
			</Typography>
		);
	};

	const data = (input: any) => {
		return {
			columns: [
				{ header: 'Identyfikator', accessor: 'id', align: 'center', width: '150px' },
				{ header: 'Punkty I', accessor: 'stage_i', align: 'center', width: '150px' },
				{ header: 'Kwalifikacja I', accessor: 'stage_i_qualified', align: 'center', width: '150px' },
				{ header: 'Punkty IIA - I', accessor: 'stage_ii_a_i', align: 'left', width: '100px' },
				{ header: 'Punkty IIA - II', accessor: 'stage_ii_a_ii', align: 'left', width: '100px' },
				{ header: 'Punkty IIA - Suma', accessor: 'stage_ii_a_sum', align: 'left', width: '100px' },
				{ header: 'Punkty IIA - Kwalifikacja', accessor: 'stage_ii_a_qualified', align: 'left', width: '100px' },
				{ header: 'Punkty IIB - Suma', accessor: 'stage_ii_b_sum', align: 'left' },
				{ header: 'Punkty IIB - Kwalifikacja', accessor: 'stage_ii_b_qualified', align: 'left' },
				{ header: 'Punkty IIIA', accessor: 'stage_iii_a', align: 'left' },
				{ header: 'Punkty IIIA - Kwalifikacja', accessor: 'stage_iii_a_qualified', align: 'left' },
				{ header: 'Punkty IIIB', accessor: 'stage_iii_b', align: 'left' },
				{ header: 'Punkty III - Suma', accessor: 'stage_iii_sum', align: 'left' },
				{ header: 'Tytuł', accessor: 'title', align: 'left' },
			],

			rows: input
				? input.map((i: Student) => {
						return {
							id: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district.id.toString().padStart(4, '0') + '-' + i.id.toString().padStart(6, '0')}
								</Typography>
							),
							stage_i: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.firstStagePoints}
								</Typography>
							),
							stage_i_qualified: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.firstStageQualified ? 'Tak' : 'Nie'}
								</Typography>
							),
							stage_ii_a_i: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.secondAOneStagePoints}
								</Typography>
							),
							stage_ii_a_ii: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.secondATwoStagePoints}
								</Typography>
							),
							stage_ii_a_sum: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.secondAOneStagePoints + i.result.secondATwoStagePoints}
								</Typography>
							),
							stage_ii_a_qualified: (isAdmin || isSecretary) && (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.secondAStageQualified ? 'Tak' : 'Nie'}
								</Typography>
							),
							stage_ii_b_sum: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.secondBOneStagePoints + i.result.secondBTwoStagePoints}
								</Typography>
							),
							stage_ii_b_qualified: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.secondBStageQualified ? 'Tak' : 'Nie'}
								</Typography>
							),
							stage_iii_a: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.thirdAStagePoints}
								</Typography>
							),
							stage_iii_a_qualified: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.thirdAStageQualified ? 'Tak' : 'Nie'}
								</Typography>
							),
							stage_iii_b: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.thirdBStageTotalPoints}
								</Typography>
							),
							stage_iii_sum: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.thirdBStageTotalPoints + i.result.thirdAStagePoints}
								</Typography>
							),
							title: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.result.finalTitle}
								</Typography>
							),
						};
					})
				: [],
		};
	};

	return (
		<>
			{isLoading ? (
				<Loader />
			) : (
				<>
					<CustomTable data={data(displayedStudents)} />
					{editedStudentAll && (
						<MangedForm>
							<EditStudentDialog
								student={editedStudentAll}
								open={!!editedStudentAll}
								onClose={handleCloseEditStudent}
							/>
						</MangedForm>
					)}
				</>
			)}
		</>
	);
};

export default StudentsStagesTable;
